import React from 'react'
import styled, {css} from 'styled-components'

//resusable button container
export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`

// Button variant types
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'success' | 'ghost'
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

// Button props interface
export interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'
  fullWidth?: boolean
  children?: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  customStyles?: React.CSSProperties & {
    [key: string] // Allow CSS selectors
  }
  className?: string
  'aria-label'?: string
  'data-testid'?: string
  [key: string] // Allow additional props
}

// Size styles using CSS custom properties
const sizeStyles = {
  xs: css`
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    line-height: 16px;
    border-radius: var(--border-radius-button, 4px);
  `,
  sm: css`
    height: 32px;
    padding: 0 12px;
    font-size: 14px;
    line-height: 20px;
    border-radius: var(--border-radius-button, 4px);
  `,
  md: css`
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    line-height: 20px;
    border-radius: var(--border-radius-button, 4px);
  `,
  lg: css`
    height: 48px;
    padding: 0 20px;
    font-size: 1rem;
    line-height: 24px;
    border-radius: var(--border-radius-button, 4px);
  `,
  xl: css`
    height: 56px;
    padding: 0 24px;
    font-size: 18px;
    line-height: 28px;
    border-radius: var(--border-radius-button, 4px);
  `,
}

// Variant styles using design system colors
const variantStyles = {
  primary: css`
    background-color: var(--primary-500, #2563eb);
    color: var(--primary-contrast-text, #ffffff);
    border: 1px solid var(--primary-500, #2563eb);

    &:hover:not(:disabled) {
      background-color: var(--primary-600, #1d4ed8);
      border-color: var(--primary-600, #1d4ed8);
    }

    &:active:not(:disabled) {
      background-color: var(--primary-700, #1e40af);
      border-color: var(--primary-700, #1e40af);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
    }
  `,
  secondary: css`
    background-color: var(--background-primary, #ffffff);
    color: var(--text-primary, #111827);
    border: 1px solid var(--border-default, #d1d5db);

    &:hover:not(:disabled) {
      background-color: var(--background-secondary, #f9fafb);
      border-color: var(--border-hover, #9ca3af);
    }

    &:active:not(:disabled) {
      background-color: var(--background-tertiary, #f3f4f6);
      border-color: var(--border-active, #6b7280);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
    }
  `,
  tertiary: css`
    background-color: transparent;
    color: var(--primary-500, #2563eb);
    border: 1px solid transparent;

    &:hover:not(:disabled) {
      background-color: var(--primary-50, #eff6ff);
    }

    &:active:not(:disabled) {
      background-color: var(--primary-100, #dbeafe);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
    }
  `,
  danger: css`
    background-color: var(--status-error, #dc2626);
    color: white;
    border: 1px solid var(--status-error, #dc2626);

    &:hover:not(:disabled) {
      background-color: var(--status-error-dark, #b91c1c);
      border-color: var(--status-error-dark, #b91c1c);
    }

    &:active:not(:disabled) {
      background-color: var(--status-error-darker, #991b1b);
      border-color: var(--status-error-darker, #991b1b);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--status-error-light, #fecaca);
    }
  `,
  success: css`
    background-color: var(--status-success, #16a34a);
    color: white;
    border: 1px solid var(--status-success, #16a34a);

    &:hover:not(:disabled) {
      background-color: var(--status-success-dark, #15803d);
      border-color: var(--status-success-dark, #15803d);
    }

    &:active:not(:disabled) {
      background-color: var(--status-success-darker, #166534);
      border-color: var(--status-success-darker, #166534);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--status-success-light, #bbf7d0);
    }
  `,
  ghost: css`
    background-color: transparent;
    color: var(--text-secondary, #6b7280);
    border: 1px solid transparent;

    &:hover:not(:disabled) {
      background-color: var(--background-secondary, #f9fafb);
      color: var(--text-primary, #111827);
    }

    &:active:not(:disabled) {
      background-color: var(--background-tertiary, #f3f4f6);
    }

    &:focus:not(:disabled) {
      box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
    }
  `,
}

// Styled button component
const StyledButton = styled.button<{
  $variant: ButtonVariant
  $size: ButtonSize
  $fullWidth: boolean
  $hasStartIcon: boolean
  $hasEndIcon: boolean
}>`
  /* Reset and base styles */
  border: none;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 200ms ease;
  outline: none;
  text-decoration: none;
  box-sizing: border-box;
  user-select: none;
  white-space: nowrap;

  /* Apply size styles */
  ${({$size}) => sizeStyles[$size]}

  /* Apply variant styles */
  ${({$variant}) => variantStyles[$variant]}

  /* Full width support */
  ${({$fullWidth}) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Loading state */
  &[aria-busy='true'] {
    pointer-events: none;
    opacity: 0.7;
  }

  /* Icon spacing adjustments */
  ${({$hasStartIcon, $hasEndIcon}) => {
    if ($hasStartIcon && $hasEndIcon) {
      return css`
        padding-left: 12px;
        padding-right: 12px;
      `
    }
    if ($hasStartIcon) {
      return css`
        padding-left: 12px;
      `
    }
    if ($hasEndIcon) {
      return css`
        padding-right: 12px;
      `
    }
    return ''
  }}

  /* Icon sizing */
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  /* Icon wrapper spans - customizable centering */
  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--button-icon-height, 16px); /* Default 16px, but overrideable */
    line-height: var(--button-icon-height, 16px);
  }
`

// Loading spinner component
const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

// Main Button component
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  startIcon,
  endIcon,
  disabled = false,
  loading = false,
  type = 'button',
  fullWidth = false,
  children,
  onClick,
  customStyles,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
  ...rest
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && onClick) {
      onClick(event)
    }
  }

  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $hasStartIcon={!!startIcon}
      $hasEndIcon={!!endIcon}
      disabled={disabled}
      type={type}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-busy={loading}
      data-testid={testId}
      className={className}
      style={customStyles}
      {...rest}>
      {loading && <LoadingSpinner />}
      {!loading && startIcon && <span>{startIcon}</span>}
      {children && <span>{children}</span>}
      {!loading && endIcon && <span>{endIcon}</span>}
    </StyledButton>
  )
}

export default Button
